import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, MessageCircle, Salad, Users, PlayCircle, ArrowLeft, Smartphone, Sparkles, X, Calculator } from "lucide-react";
import InstallAppModal, { usePwaInstall } from "@/components/InstallAppModal";
import PushOptInBanner from "@/components/PushOptInBanner";

// Direct WhatsApp handle for the coach — replaces the deprecated in-app chat.
const COACH_WHATSAPP_URL = "https://wa.me/966538069848";

export default function DashboardPage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [showInstall, setShowInstall] = useState(false);
  const { standalone } = usePwaInstall();
  const [installDismissed, setInstallDismissed] = useState(
    () => localStorage.getItem("install_prompt_dismissed") === "1"
  );
  const showInstallCard = !standalone && !installDismissed;

  const dismissInstall = () => {
    localStorage.setItem("install_prompt_dismissed", "1");
    setInstallDismissed(true);
  };

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/recipes/favorites").then((r) => setFavorites(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 max-w-3xl mx-auto lg:mx-0 lg:max-w-none">
      <div className="animate-fade-in-up">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-semibold" data-testid="dashboard-welcome">
          مرحبًا {user?.full_name?.split(" ")[0]} 👋
        </h1>
        <p className="text-muted-foreground mt-2 text-sm sm:text-base">هذه لوحتك الرئيسية، تابع تقدمك وتعرّف على المستجدات.</p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <StatCard icon={BookOpen} label="دوراتي" value={courses.length} to="/courses" testid="stat-courses" />
        <StatCard icon={Salad} label="مفضلاتي" value={favorites.length} to="/recipes" testid="stat-favs" />
        <StatCard icon={Users} label="المجتمع" value="نشط" to="/community" testid="stat-community" />
        <StatCard icon={MessageCircle} label="تواصل مع المدرب" value="واتساب" href={COACH_WHATSAPP_URL} testid="stat-chat" />
      </div>

      {/* Install-as-app card — hidden if already installed or dismissed */}
      {showInstallCard && (
        <div
          className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50 dark:from-orange-950/30 dark:via-amber-950/20 dark:to-rose-950/30 p-4 sm:p-5 animate-fade-in-up"
          data-testid="install-app-card"
        >
          <button
            onClick={dismissInstall}
            className="absolute top-2 end-2 h-7 w-7 grid place-items-center rounded-full text-muted-foreground/60 hover:text-foreground hover:bg-foreground/5"
            aria-label="إخفاء"
            data-testid="btn-dismiss-install"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 grid place-items-center shadow-md shadow-orange-500/20">
              <Smartphone className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0 pe-6">
              <div className="font-display font-semibold text-sm sm:text-base flex items-center gap-1.5">
                حمّل التطبيق على جوالك
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                وصول سريع من شاشتك الرئيسية بدون شريط المتصفح — يعمل على iPhone وAndroid.
              </p>
              <Button
                onClick={() => setShowInstall(true)}
                size="sm"
                className="mt-3 rounded-full gap-1.5"
                data-testid="btn-open-install-modal"
              >
                <Smartphone className="h-3.5 w-3.5" />
                طريقة التثبيت
              </Button>
            </div>
          </div>
        </div>
      )}

      <InstallAppModal open={showInstall} onOpenChange={setShowInstall} />

      {/* Push notification opt-in — dismissable, hidden once subscribed */}
      <PushOptInBanner />

      {/* Continue learning */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg sm:text-xl font-display font-semibold">أكمل تعلّمك</h2>
          <Link to="/courses"><Button variant="ghost" size="sm" className="gap-1">عرض الكل <ArrowLeft className="h-3 w-3 rtl:-scale-x-100" /></Button></Link>
        </div>
        {courses.length === 0 ? (
          <EmptyState text="لا توجد دورات مسجّلة بعد. سيتم إضافتها تلقائيًا بعد الشراء." />
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {courses.slice(0, 4).map((c) => (
              <Link key={c.id} to={`/courses/${c.id}`} data-testid={`dashboard-course-${c.id}`}>
                <Card className="overflow-hidden hover:-translate-y-1 transition-transform h-full">
                  <div className="flex gap-3">
                    <img src={c.cover_image} alt={c.title} className="w-28 sm:w-32 aspect-[4/5] object-cover shrink-0" />
                    <CardContent className="flex-1 p-3 sm:p-4 min-w-0">
                      <h3 className="font-display font-semibold line-clamp-2 text-sm sm:text-base">{c.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1 truncate">{c.instructor_name} • {c.lessons_count} دروس</p>
                      <div className="mt-3 space-y-1">
                        <Progress value={c.progress_pct} />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>{c.progress_pct}%</span>
                          <span className="flex items-center gap-1"><PlayCircle className="h-3 w-3" /> متابعة</span>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Quick links row */}
      <section className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <QuickLink to="/recipes" icon={Salad} title="وصفات صحية" desc="استكشف وصفات جديدة" />
        <QuickLink to="/calories" icon={Calculator} title="احسب سعراتك" desc="سعرات وماكروز مخصصة" />
        <QuickLink to="/community" icon={Users} title="المجتمع" desc="شارك تجربتك" />
        <QuickLink href={COACH_WHATSAPP_URL} icon={MessageCircle} title="تواصل مع المدرب" desc="واتساب مباشر" />
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, to, href, testid }) {
  // Support both internal (`to`) and external (`href`) targets. External links
  // open in a new tab — used for the coach WhatsApp shortcut.
  const inner = (
    <Card className="p-3 sm:p-4 hover:-translate-y-1 transition-transform h-full">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">{label}</p>
          <p className="text-xl sm:text-2xl font-display font-semibold mt-1 truncate">{value}</p>
        </div>
        <div className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 rounded-lg bg-secondary grid place-items-center">
          <Icon className="h-4 w-4 text-primary rtl:-scale-x-100" />
        </div>
      </div>
    </Card>
  );
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}>{inner}</a>;
  }
  return <Link to={to} data-testid={testid}>{inner}</Link>;
}

function QuickLink({ to, href, icon: Icon, title, desc, highlight }) {
  const inner = (
    <Card className={`p-4 hover:-translate-y-1 transition-transform h-full flex items-center gap-3 ${highlight ? "ring-2 ring-primary/50" : ""}`}>
      <div className="h-10 w-10 shrink-0 rounded-lg bg-primary/10 text-primary grid place-items-center">
        <Icon className="h-5 w-5 rtl:-scale-x-100" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display font-semibold text-sm truncate">{title}</p>
        <p className="text-xs text-muted-foreground truncate">{desc}</p>
      </div>
      <ArrowLeft className="h-4 w-4 text-muted-foreground rtl:-scale-x-100 shrink-0" />
    </Card>
  );
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer">{inner}</a>;
  }
  return <Link to={to}>{inner}</Link>;
}

function EmptyState({ text }) {
  return <div className="p-6 sm:p-8 rounded-xl border border-dashed text-center text-muted-foreground text-sm">{text}</div>;
}
